import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { FilterButton, InfinityLoader, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { applyEventsSearchCriteria } from "domains/events/actions";
import { EventDetail } from "domains/events/components/EventDetail";
import { EventListItem } from "domains/events/components/EventListItem";
import {
  createEventsForTopicSelector,
  getEventFilterCount,
  getEventsSearchCriteria,
  getIsEventsFiltered,
} from "domains/events/selectors";
import { activateModal } from "domains/modal/actions";
import { getTopicPageEvent, getTopicRelatedItemDetailPageEvent } from "domains/topic/analytics";
import { getTopicTitle, useTopicNavigation } from "domains/topicDetail/utils";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { useTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { fetchEventById } from "entities/events/actions";
import { createEventSelector, createEventsSelector } from "entities/events/selectors";
import { getCanAccessTopicEvents } from "entities/permissions/selectors";
import { fetchTopic } from "entities/topics/actions";
import { createTopicSelector, getTopic } from "entities/topics/selectors";

export const TopicEventDataPage = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { eventId, keyIssueId, topicId } = useParams();
  const eventAccess = useSelector(getCanAccessTopicEvents);
  const filterCount = useSelector(getEventFilterCount);
  const { search } = useSelector(getEventsSearchCriteria);
  const isFiltered = useSelector(getIsEventsFiltered);
  const { hasFilters } = useTopicContentMeta("events");
  const track = useTrack();
  const translate = useTranslate();

  if (topicId === undefined) throw new Error("topicId param undefined in TopicEventDataPage");
  const selectedTopicId = keyIssueId ?? topicId;

  const eventsForTopic = useSelector(createEventsForTopicSelector(selectedTopicId));
  const topic = useSelector(createTopicSelector(topicId));
  const keyIssue = useSelector(getTopic(keyIssueId));
  const { path } = useTopicNavigation({ id: topicId, keyIssueId });

  const eventsSelector = useMemo(() => createEventsSelector(eventsForTopic?.data ?? []), [eventsForTopic?.data]);

  const events = useSelector(eventsSelector);

  const id = getPageId(eventId, events);

  const event = useSelector(createEventSelector(id));

  const hasNextPage = eventsForTopic?.hasNextPage ?? true;
  const isPending = eventsForTopic?.isFetching ?? false;
  const selectedTopic = keyIssueId ? keyIssue : topic;

  useEffect(() => {
    if (id) {
      dispatch(fetchEventById({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!topic || (keyIssueId && !keyIssue)) {
      return;
    }
    const event = getTopicPageEvent(topic, keyIssue, "Event List");
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssue?.id, topic?.id, track]);

  useEffect(() => {
    if (!selectedTopic || !event) {
      return;
    }
    const analyticsEvent = getTopicRelatedItemDetailPageEvent("Session detail", event, "Session", selectedTopic);
    track(analyticsEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, selectedTopic?.id, track]);

  useEffect(() => {
    if (events.length === 0 || isPending) {
      dispatch(
        applyEventsSearchCriteria({
          topicId: selectedTopicId,
          offset: 0,
          limit: 20,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(fetchTopic({ id: topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  useEffect(() => {
    if (!eventAccess) {
      go("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventAccess]);

  const getLink = (eventId: string) => `${path}/events/${eventId}`;

  const loadNextPage = () => {
    dispatch(
      applyEventsSearchCriteria({
        topicId: selectedTopicId,
        offset: events.length,
        limit: 20,
      }),
    );
  };

  let emptyMessage = isFiltered
    ? translate("app.discover.topic.tabs.events.no_data")
    : `${translate("app.topic.no_content")} ${translate("app.discover.topic.tabs.events.title")}.`;

  if (!topic?.id) {
    emptyMessage = translate("app.no_access.topic_content");
  }

  const list =
    events.length === 0 && !isPending ? (
      <p>{emptyMessage}</p>
    ) : (
      <InfinityLoader hasNextPage={hasNextPage} isPending={isPending} loadNextPage={loadNextPage}>
        {events.map((item) => (
          <EventListItem key={item.id} event={item} isSelected={eventId === item.id} link={getLink(item.id)} />
        ))}
      </InfinityLoader>
    );

  const title = translate("app.discover.topic.tabs.events.title");

  const onFilterClick = () => dispatch(activateModal({ id: "TopicEventFilter", params: { selectedTopicId, topicId } }));

  const topicFilterButton =
    topic?.id && hasFilters ? <FilterButton onClick={onFilterClick} count={filterCount} /> : undefined;

  const onSearchHandle = (search: string) => dispatch(applyEventsSearchCriteria({ topicId: selectedTopicId, search }));

  const searchElement = topic?.id ? (
    <TopicSearchBar onSearch={onSearchHandle} isPending={isPending} defaultValue={search} />
  ) : undefined;

  return (
    <MasterDetailLayout
      listTitle={title}
      listComponent={list}
      listBackNavigation={{
        label: getTopicTitle(topic, keyIssue) ?? "",
        path,
      }}
      detailBackNavigation={{
        label: title,
        path: `${path}/events`,
      }}
      mainView={eventId ? "right" : "left"}
      listHeaderRightElement={topicFilterButton}
      headerElement={searchElement}
    >
      <EventDetail event={event} shareLink={`/topics/${topicId}/events/${id}`} />
    </MasterDetailLayout>
  );
};
